import { useState, useCallback, useEffect } from 'react';
import {
  MedicalDataFile,
  DashboardStats,
  FilterOptions,
  FileActionEvent,
} from '../types/medicalDataList';

// ローカルストレージのキー
const STORAGE_KEY = 'mederi_medical_data_files';

// モックデータ生成関数
const generateMockData = (): MedicalDataFile[] => {
  const statuses: MedicalDataFile['status'][] = ['uploaded', 'processing', 'completed', 'error'];
  const fileNames = [
    '診療データ_2024年1月.csv',
    '患者リスト_東京院.csv',
    '予約管理_2024Q1.csv',
    '診療記録_大阪院.csv',
    'mederi_data_export_20240315.csv',
  ];

  return Array.from({ length: 15 }, (_, index) => ({
    id: `file-${index + 1}`,
    fileName: fileNames[index % fileNames.length].replace('1', String((index % 12) + 1)),
    uploadedAt: new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000).toISOString(),
    uploadedBy: ['山田太郎', '佐藤花子', '鈴木一郎'][index % 3],
    recordCount: Math.floor(Math.random() * 5000) + 100,
    status: statuses[Math.floor(Math.random() * statuses.length)],
    fileSize: Math.floor(Math.random() * 10 * 1024 * 1024) + 1024 * 1024,
    lastModified: new Date(Date.now() - Math.random() * 10 * 24 * 60 * 60 * 1000).toISOString(),
    processingProgress: statuses[index % statuses.length] === 'processing' ? Math.floor(Math.random() * 80) + 20 : undefined,
    tags: ['医療', '診療', '予約'].slice(0, Math.floor(Math.random() * 3) + 1),
  }));
};

export const useMedicalDataList = () => {
  const [files, setFiles] = useState<MedicalDataFile[]>([]);
  const [filteredFiles, setFilteredFiles] = useState<MedicalDataFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({
    sortBy: 'uploadedAt',
    sortOrder: 'desc',
  });

  // ローカルストレージからデータを読み込む
  const loadFiles = useCallback(() => {
    try {
      const storedData = localStorage.getItem(STORAGE_KEY);
      if (storedData) {
        const parsedData = JSON.parse(storedData);
        setFiles(parsedData);
      } else {
        // 初回はモックデータを生成
        const mockData = generateMockData();
        setFiles(mockData);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(mockData));
      }
    } catch (error) {
      console.error('Failed to load files:', error);
      setFiles([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // ローカルストレージにデータを保存
  const saveFiles = useCallback((updatedFiles: MedicalDataFile[]) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedFiles));
      setFiles(updatedFiles);
    } catch (error) {
      console.error('Failed to save files:', error);
    }
  }, []);

  // 統計情報を計算
  const calculateStats = useCallback((fileList: MedicalDataFile[]): DashboardStats => {
    const totalFiles = fileList.length;
    const completedFiles = fileList.filter(f => f.status === 'completed').length;
    const processingFiles = fileList.filter(f => f.status === 'processing').length;
    const errorFiles = fileList.filter(f => f.status === 'error').length;
    const totalRecords = fileList.reduce((sum, f) => sum + f.recordCount, 0);
    const storageUsed = fileList.reduce((sum, f) => sum + f.fileSize, 0);

    // トレンドデータ（モック）
    const generateTrendData = (days: number) => {
      return Array.from({ length: days }, (_, i) => {
        const date = new Date();
        date.setDate(date.getDate() - (days - i - 1));
        return {
          date: date.toISOString().split('T')[0],
          uploads: Math.floor(Math.random() * 10) + 1,
          records: Math.floor(Math.random() * 1000) + 100,
        };
      });
    };

    return {
      totalFiles,
      completedFiles,
      processingFiles,
      errorFiles,
      totalRecords,
      storageUsed,
      trends: {
        daily: generateTrendData(7),
        weekly: generateTrendData(4),
        monthly: generateTrendData(12),
      },
    };
  }, []);

  // フィルタリングとソート
  const applyFiltersAndSort = useCallback(() => {
    let result = [...files];

    // フィルタリング
    if (filterOptions.status && filterOptions.status.length > 0) {
      result = result.filter(file => filterOptions.status!.includes(file.status));
    }

    if (filterOptions.searchQuery) {
      const query = filterOptions.searchQuery.toLowerCase();
      result = result.filter(file =>
        file.fileName.toLowerCase().includes(query) ||
        file.uploadedBy?.toLowerCase().includes(query) ||
        file.tags?.some(tag => tag.toLowerCase().includes(query))
      );
    }

    if (filterOptions.dateRange) {
      const { start, end } = filterOptions.dateRange;
      result = result.filter(file => {
        const uploadDate = new Date(file.uploadedAt);
        return uploadDate >= start && uploadDate <= end;
      });
    }

    // ソート
    if (filterOptions.sortBy) {
      result.sort((a, b) => {
        const aValue = a[filterOptions.sortBy!];
        const bValue = b[filterOptions.sortBy!];

        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return filterOptions.sortOrder === 'asc'
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        }

        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return filterOptions.sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
        }

        return 0;
      });
    }

    setFilteredFiles(result);
  }, [files, filterOptions]);

  // ファイルを追加
  const addFile = useCallback((file: MedicalDataFile) => {
    const updatedFiles = [file, ...files];
    saveFiles(updatedFiles);
  }, [files, saveFiles]);

  // ファイルを更新
  const updateFile = useCallback((id: string, updates: Partial<MedicalDataFile>) => {
    const updatedFiles = files.map(file =>
      file.id === id ? { ...file, ...updates } : file
    );
    saveFiles(updatedFiles);
  }, [files, saveFiles]);

  // ファイルを削除
  const deleteFile = useCallback((id: string) => {
    const updatedFiles = files.filter(file => file.id !== id);
    saveFiles(updatedFiles);
  }, [files, saveFiles]);

  // 複数ファイルを削除
  const deleteFiles = useCallback((ids: string[]) => {
    const updatedFiles = files.filter(file => !ids.includes(file.id));
    saveFiles(updatedFiles);
  }, [files, saveFiles]);

  // ファイルアクションを処理
  const handleFileAction = useCallback((event: FileActionEvent) => {
    switch (event.action) {
      case 'delete':
        deleteFile(event.file.id);
        break;
      case 'edit':
        // 編集モーダルを開く処理
        console.log('Edit file:', event.file);
        break;
      case 'view':
        // 詳細モーダルを開く処理
        console.log('View file:', event.file);
        break;
      case 'download':
        // ダウンロード処理
        console.log('Download file:', event.file);
        break;
      case 'share':
        // 共有処理
        console.log('Share file:', event.file);
        break;
    }
  }, [deleteFile]);

  // フィルターオプションを更新
  const updateFilterOptions = useCallback((options: Partial<FilterOptions>) => {
    setFilterOptions(prev => ({ ...prev, ...options }));
  }, []);

  // 初回読み込み
  useEffect(() => {
    loadFiles();
  }, [loadFiles]);

  // フィルタリング実行
  useEffect(() => {
    applyFiltersAndSort();
  }, [applyFiltersAndSort]);

  // 統計情報更新
  useEffect(() => {
    setStats(calculateStats(files));
  }, [files, calculateStats]);

  return {
    files,
    filteredFiles,
    loading,
    stats,
    filterOptions,
    addFile,
    updateFile,
    deleteFile,
    deleteFiles,
    handleFileAction,
    updateFilterOptions,
    refresh: loadFiles,
  };
};